import {
	PropsWithChildren,
	ReactElement,
	useContext
} from "react";
import { ActionListItem } from "../../ActionList/ActionListItem";
import { FontIconName, SvgIconValue } from "../../Icon/Icon";
import { ButtonBase } from "../../ButtonBase/ButtonBase";
import {
	CollapsibleListItemContext,
	CollapsibleListItemRenderMode
} from "../../CollapsibleList/CollapsibleList";
import styles from "./ActionBarButton.module.less";
import { ActionBarButtonIcon } from "../ActionBarButtonIcon/ActionBarButtonIcon";
import { useCallbackWithoutArguments, useKeyboardListNavigationItem } from "client/Hooks";
import { Key } from "client/Misc/commonTypes";

export type ActionBarButtonProps = {
	icon?: FontIconName | SvgIconValue;
	text: string;
	onClick: () => void;
	trackingId?: string;
};

export const ActionBarButton = (props: ActionBarButtonProps): ReactElement => {
	const {
		icon,
		text,
		onClick,
		trackingId
	} = props;

	const {
		renderMode,
		index,
		tabIndex
	} = useContext(CollapsibleListItemContext);

	const clickHandler: (() => void) | undefined = useCallbackWithoutArguments(onClick);

	if (renderMode === CollapsibleListItemRenderMode.Collapsed) {
		return (
			<ActionListItem
				text={text}
				icon={icon}
				onClick={onClick}
				trackingId={trackingId}
			/>
		);
	}

	return (
		<NormalButton
			onClick={clickHandler}
			trackingId={trackingId}
			icon={icon}
			tabIndex={tabIndex}
			index={index}
		>
			{text}
		</NormalButton>
	);
};

ActionBarButton.displayName = "ActionBarButton";

type NormalButtonProps = {
	onClick: () => void;
	trackingId: string | undefined;
	icon?: FontIconName | SvgIconValue;
	index: number;
	tabIndex: number;
};

const NormalButton = (props: PropsWithChildren<NormalButtonProps>): ReactElement => {
	const {
		children,
		onClick,
		trackingId,
		icon,
		index,
		tabIndex
	} = props;

	const {
		ref,
		onKeyDown
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute: onClick,
		executeKey: [Key.Enter, Key.Space]
	});

	return (
		<ButtonBase
			className={styles.root}
			onClick={onClick}
			ref={ref}
			onKeyDown={onKeyDown}
			trackingId={trackingId}
			tabIndex={tabIndex}
		>
			{icon && (
				<ActionBarButtonIcon icon={icon} />
			)}
			<span>
				{children}
			</span>
		</ButtonBase>
	);
};
NormalButton.displayName = "NormalButton";
